import { type InputHTMLAttributes, type ReactNode, forwardRef } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

import { cn } from '../lib/utils';

// The bordered surface. It sits on the input itself normally, and moves out to
// a wrapper when a leading icon has to share the box.
const surface = [
  'rounded-md border border-uniform-gray-300 bg-white',
  'dark:border-uniform-gray-600 dark:bg-uniform-gray-800'
];

const inputVariants = tv({
  slots: {
    root: 'flex flex-col gap-1',
    label: 'text-sm font-medium text-uniform-gray-700 dark:text-uniform-gray-300',
    inputWrap: 'flex items-center gap-2',
    field: 'contents',
    icon: 'flex-none text-sm text-uniform-gray-400 dark:text-uniform-gray-500',
    input: [
      ...surface,
      'w-full px-3 py-1.5 text-sm',
      'text-uniform-gray-900 placeholder:text-uniform-gray-400',
      'dark:text-uniform-gray-100',
      'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
      'disabled:cursor-not-allowed disabled:bg-uniform-gray-50 disabled:text-uniform-gray-400',
      'dark:disabled:bg-uniform-gray-900'
    ],
    append: 'shrink-0 text-sm text-uniform-gray-600 dark:text-uniform-gray-400',
    help: 'text-xs text-uniform-gray-600 dark:text-uniform-gray-400',
    error: 'text-xs text-red-500 dark:text-red-400'
  },
  variants: {
    hasError: {
      true: {
        input: ['border-red-500 dark:border-red-400', 'focus:border-red-500 focus:ring-red-500'],
        field:
          'border-red-500 focus-within:border-red-500 focus-within:ring-red-500 dark:border-red-400'
      }
    },
    hideLabel: {
      // Still announced, just not drawn — for compact controls (search boxes,
      // inline edits) whose purpose is clear from context to sighted users.
      true: { label: 'sr-only' }
    },
    hasLeadingIcon: {
      true: {
        field: [
          ...surface,
          'flex w-full items-center gap-2 px-3 py-1.5',
          'focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500',
          'has-[:disabled]:cursor-not-allowed has-[:disabled]:bg-uniform-gray-50',
          'dark:has-[:disabled]:bg-uniform-gray-900'
        ],
        input: [
          'w-full border-0 bg-transparent p-0 text-sm',
          'text-uniform-gray-900 placeholder:text-uniform-gray-400',
          'dark:text-uniform-gray-100',
          'focus:outline-none focus:ring-0',
          'disabled:cursor-not-allowed disabled:text-uniform-gray-400'
        ]
      }
    }
  }
});

type InputVariants = VariantProps<typeof inputVariants>;

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  helpText?: string;
  /** Marks the field invalid and renders the message in place of `helpText`. */
  error?: string;
  appendedText?: string;
  leadingIcon?: ReactNode;
  /** Keeps the label for screen readers but removes it from the layout. */
  hideLabel?: boolean;
  inputClassName?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helpText,
      error,
      appendedText,
      leadingIcon,
      hideLabel,
      inputClassName,
      id,
      className,
      ...props
    },
    ref
  ) => {
    const vars = inputVariants({ hideLabel, hasLeadingIcon: !!leadingIcon, hasError: !!error });
    return (
      <div className={cn(vars.root(), className)}>
        {label && (
          <label htmlFor={id} className={vars.label()}>
            {label}
          </label>
        )}
        <div className={vars.inputWrap()}>
          <div className={vars.field()}>
            {leadingIcon && <span className={vars.icon()}>{leadingIcon}</span>}
            <input
              ref={ref}
              id={id}
              aria-invalid={error ? true : undefined}
              className={cn(vars.input(), inputClassName)}
              {...props}
            />
          </div>
          {appendedText && <span className={vars.append()}>{appendedText}</span>}
        </div>
        {error ? (
          <p className={vars.error()}>{error}</p>
        ) : (
          helpText && <p className={vars.help()}>{helpText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input, inputVariants };
export type { InputProps, InputVariants };
